import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import {
   Grid,
   TextField,
   Box,
   Typography,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   Button,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { API_URL } from '../../Config'
import { toast } from 'react-toastify'
import { getAuthToken } from '../../Redux/Service/FetchBaseQuery'
import StyledDashboardLayout from '../style'
import { useGetProvidersQuery } from '../../Redux/Service/ProviderApi'
import API from '../../Web/Instance'
import { useGetCurrenciesQuery } from '../../Redux/Service/CurrencyApi'

const EditQARate = () => {
   const navigate = useNavigate()
   const { id } = useParams()
   const [techStacks, setTechStacks] = useState([])
   const [symbol, setSymbol] = useState('$')
   const [loading, setLoading] = useState(true)
   const [initialValues, setInitialValues] = useState(null)

   // Providers
   const {
      data: providerData,
      isLoading: isLoadingForProvider,
      isError: isErrorForProvider,
      error: errorForProvider,
   } = useGetProvidersQuery()

   // Currencies
   const {
      data: currencies,
      isLoading: isLoadingForCurrency,
      error: errorForCurrency,
   } = useGetCurrenciesQuery()

   // Fetch tech stacks
   useEffect(() => {
      API.get(`technology-stack/list-technology`).then((response) => {
         setTechStacks(response.data?.response || [])
      })
   }, [])

   // Fetch single QA hourly rate data
   useEffect(() => {
      const fetchHourlyRate = async () => {
         setLoading(true)
         try {
            const token = getAuthToken()
            const res = await axios.get(
               `${API_URL}hourlyrate/hourly-rate-QA/${id}`,
               {
                  headers: { Authorization: `Bearer ${token}` },
               },
            )
            const data = res.data?.data
            setInitialValues({
               serviceProviderId: data?.serviceProvider?.id || '',
               techStackId: data?.techStack?.id || '',
               currencyId: data?.currency?.id || '',
               client: data?.client || '',
               // hourlyRateText: data?.hourlyRateText || "",
               description: data?.description || '',
               QA: data?.QA ?? '',
               PM: data?.PM ?? '',
               Devops: data?.Devops ?? '',
            })
            if (data?.currency?.symbol) setSymbol(data.currency.symbol)
         } catch (error) {
            toast.error('Failed to fetch QA hourly rate data')
         } finally {
            setLoading(false)
         }
      }
      fetchHourlyRate()
   }, [id])

   const currencySymbolSet = (currencyId, setFieldValue) => {
      setFieldValue('currencyId', currencyId)
      const selectedCurrency = currencies?.response?.find(
         (val) => val.id === currencyId,
      )
      setSymbol(selectedCurrency?.symbol ?? '$')
   }

   // Add validation schema for number fields
   const validationSchema = Yup.object({
      serviceProviderId: Yup.string().required('Service Provider is required'),
      techStackId: Yup.string().required('Tech Stack is required'),
      currencyId: Yup.string().required('Currency is required'),
      client: Yup.string().required('Client is required').max(50, "Client should be atmost 50 characters long"),
      // hourlyRateText: Yup.number()
      //   .positive("Hourly rate must be a positive number")
      //   .required("Hourly rate is required"),
      description: Yup.string().max(1000, 'Description must be at most 1000 characters'),
      QA: Yup.number()
         .positive('QA rate must be a positive number')
         .required('QA rate is required')
         .max(99999, 'QA rate must be at most 99999'),
      PM: Yup.number()
         .positive('PM rate must be a positive number')
         .required('PM rate is required')
         .max(99999, "PM rate must be at most 99999"),
      Devops: Yup.number()
         .positive('Devops rate must be a positive number')
         .required('Devops rate is required')
         .max(99999, "Devops rate must be at most 99999"),
   })

   // PATCH update for QA hourly rate
   const handleSubmit = async (values, { setSubmitting }) => {
      try {
         const token = getAuthToken()

         // Convert string values to numbers before sending
         const payload = {
            ...values,
            // hourlyRateText: parseFloat(values.hourlyRateText),
            QA: parseFloat(values.QA),
            PM: parseFloat(values.PM),
            Devops: parseFloat(values.Devops),
         }

         const res = await axios.patch(
            `${API_URL}hourlyrate/update-hourly-rate-QA/${id}`,
            payload,
            {
               headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
               },
            },
         )
         toast.success(res?.data?.message || 'QA Hourly Rate updated')
         navigate('/hourly-rate/qa-rate-list')
      } catch (error) {
         toast.error(
            error?.response?.data?.message || 'Failed to update QA rate',
         )
      } finally {
         setSubmitting(false)
      }
   }

   if (
      loading ||
      isLoadingForProvider ||
      isLoadingForCurrency ||
      !initialValues
   ) {
      return <div>Loading...</div>
   }

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">Edit QA, PM, DevOps Rate</h1>
                     </div>
                     <div className="btnWrap">
                        <Link
                           to="/hourly-rate/qa-rate-list"
                           className="btn addBtn"
                        >
                           <ArrowBackIcon /> Back
                        </Link>
                     </div>
                  </div>
               </div>
            </div>
            <div className="card customCard p-3 m-3">
               <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  enableReinitialize
                  onSubmit={handleSubmit}
               >
                  {({
                     values,
                     setFieldValue,
                     touched,
                     errors,
                     isSubmitting,
                     handleBlur,
                  }) => (
                     <Form>
                        <Box className="card-body" sx={{ padding: '15px 0' }}>
                           <Grid container spacing={2}>
                              <Grid item md={6} xs={12}>
                                 {isErrorForProvider ? (
                                    <div>
                                       Error: {errorForProvider?.message}
                                    </div>
                                 ) : (
                                    <FormControl fullWidth>
                                       <InputLabel id="service-provider-label">
                                          Service Provider
                                       </InputLabel>
                                       <Field
                                          name="serviceProviderId"
                                          as={Select}
                                          labelId="service-provider-label"
                                          label="Service Provider"
                                          onBlur={handleBlur}
                                       >
                                          <MenuItem value="" disabled>
                                             Select Service Provider
                                          </MenuItem>
                                          {providerData?.response?.map(
                                             (val, index) => (
                                                <MenuItem
                                                   key={index}
                                                   value={val.id}
                                                >
                                                   {val.name}
                                                </MenuItem>
                                             ),
                                          )}
                                       </Field>
                                       <ErrorMessage
                                          name="serviceProviderId"
                                          className="error-message"
                                          component="div"
                                          style={{ color: 'red' }}
                                       />
                                    </FormControl>
                                 )}
                              </Grid>
                              <Grid item xs={12} md={6}>
                                 <FormControl fullWidth>
                                    <InputLabel id="techstack-label">
                                       Tech Stack
                                    </InputLabel>
                                    <Field
                                       name="techStackId"
                                       as={Select}
                                       labelId="techstack-label"
                                       label="Tech Stack"
                                       onBlur={handleBlur}
                                    >
                                       <MenuItem value="" disabled>
                                          Select Tech Stack
                                       </MenuItem>
                                       {techStacks.map((ts) => (
                                          <MenuItem key={ts.id} value={ts.id}>
                                             {ts.technology}
                                          </MenuItem>
                                       ))}
                                    </Field>
                                    <ErrorMessage
                                       name="techStackId"
                                       component="div"
                                       style={{ color: 'red' }}
                                    />
                                 </FormControl>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                 <FormControl fullWidth>
                                    <InputLabel id="currency-label">
                                       Currency
                                    </InputLabel>
                                    <Field
                                       label="Currency"
                                       name="currencyId"
                                       as={Select}
                                       labelId="currency-label"
                                       onChange={(event) =>
                                          currencySymbolSet(
                                             event.target.value,
                                             setFieldValue,
                                          )
                                       }
                                    >
                                       {currencies?.response?.map(
                                          (val, index) => (
                                             <MenuItem
                                                key={index}
                                                value={val.id}
                                             >
                                                {val.name} ({val.code})
                                             </MenuItem>
                                          ),
                                       )}
                                    </Field>
                                 </FormControl>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                 <Field
                                    fullWidth
                                    label="Client"
                                    name="client"
                                    as={TextField}
                                    variant="outlined"
                                    onBlur={handleBlur}
                                    error={
                                       touched.client && Boolean(errors.client)
                                    }
                                    helperText={touched.client && errors.client}
                                 />
                              </Grid>
                              {/* <Grid item xs={12} md={6}>
                      <Field
                        fullWidth
                        label={`Hourly Rate (${symbol})`}
                        name="hourlyRateText"
                        as={TextField}
                        variant="outlined"
                        type="number"
                        inputProps={{
                          min: "0",
                          step: "0.01",
                        }}
                        onBlur={handleBlur}
                        error={
                          touched.hourlyRateText && Boolean(errors.hourlyRateText)
                        }
                        helperText={touched.hourlyRateText && errors.hourlyRateText}
                      />
                    </Grid> */}
                              <Grid item xs={12}>
                                 <Field
                                    fullWidth
                                    label="Description"
                                    name="description"
                                    as={TextField}
                                    variant="outlined"
                                    multiline
                                    rows={8}
                                    onBlur={handleBlur}
                                    error={
                                       touched.description &&
                                       Boolean(errors.description)
                                    }
                                    helperText={
                                       touched.description && errors.description
                                    }
                                 />
                                 {values?.description &&
                                    <Typography sx={{ fontSize: '12px', color: "grey" }}>
                                       {values?.description?.length} characters
                                    </Typography>
                                 }
                              </Grid>
                              <Grid item xs={12}>
                                 <Typography variant="h6" gutterBottom>
                                    Resource Rates ({symbol}/hour)
                                 </Typography>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                 <Field
                                    fullWidth
                                    label={`QA Rate (${symbol})`}
                                    name="QA"
                                    as={TextField}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                       min: '0',
                                       step: '0.01',
                                    }}
                                    onBlur={handleBlur}
                                    error={touched.QA && Boolean(errors.QA)}
                                    helperText={touched.QA && errors.QA}
                                 />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                 <Field
                                    fullWidth
                                    label={`PM Rate (${symbol})`}
                                    name="PM"
                                    as={TextField}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                       min: '0',
                                       step: '0.01',
                                    }}
                                    onBlur={handleBlur}
                                    error={touched.PM && Boolean(errors.PM)}
                                    helperText={touched.PM && errors.PM}
                                 />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                 <Field
                                    fullWidth
                                    label={`DevOps Rate (${symbol})`}
                                    name="Devops"
                                    as={TextField}
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                       min: '0',
                                       step: '0.01',
                                    }}
                                    onBlur={handleBlur}
                                    error={
                                       touched.Devops && Boolean(errors.Devops)
                                    }
                                    helperText={touched.Devops && errors.Devops}
                                 />
                              </Grid>
                              <Grid item xs={12}>
                                 <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting}
                                    className="commonBtn"
                                 >
                                    {isSubmitting ? 'Saving...' : 'Save'}
                                 </Button>
                              </Grid>
                           </Grid>
                        </Box>
                     </Form>
                  )}
               </Formik>
            </div>
         </div>
      </StyledDashboardLayout>
   )
}

export default EditQARate
